import React, { useState, useRef } from 'react';
import { Camera, Upload, X, Send, Info, Shield, Zap } from 'lucide-react';
import { EncryptedImageStorageService } from '../services/EncryptedImageStorageService';
import { useAuth } from '../contexts/AuthContext';
import { WebTeamsPhotoService } from '../services/TeamsPhotoService';
import { getApp } from 'firebase/app';
import { getDatabase } from 'firebase/database';

interface PhotoUploadProps {
    challengeId: string;
    onPhotoUploaded?: (photoId: string) => void;
    className?: string;
}

export const PhotoUpload: React.FC<PhotoUploadProps> = ({
    challengeId,
    onPhotoUploaded,
    className = ''
}) => {
    const [isUploading, setIsUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [caption, setCaption] = useState('');
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [isServiceReady, setIsServiceReady] = useState(false);
    const [optimizationStats, setOptimizationStats] = useState<{
        originalSize: number;
        optimizedSize: number;
        compressionRatio: number;
        dimensions: string;
    } | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const { user } = useAuth();
    const encryptedImageStorageService = EncryptedImageStorageService.getInstance();
    const teamsPhotoService = WebTeamsPhotoService.getInstance();

    // Initialize the encrypted image storage service and Firebase service
    React.useEffect(() => {
        const initializeService = async () => {
            try {
                await encryptedImageStorageService.initialize();

                // Initialize Firebase service
                const app = getApp();
                const database = getDatabase(app);
                teamsPhotoService.getTeamsPhotoFirebaseService().setDatabase(database);

                setIsServiceReady(true);
            } catch (error) {
                console.error('Failed to initialize services:', error);
                setError('Failed to initialize services');
            }
        };

        initializeService();
    }, [encryptedImageStorageService, teamsPhotoService]);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError('Please select an image file');
                return;
            }

            // Validate file size (10MB limit for original files)
            if (file.size > 10 * 1024 * 1024) {
                setError('Image size must be less than 10MB');
                return;
            }

            setSelectedFile(file);
            setError(null);
            setOptimizationStats(null);

            // Create preview URL
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            setShowUploadForm(true);
        }
    };

    const handleCameraClick = () => {
        // For mobile devices, this could open the camera
        // For now, just trigger file input
        fileInputRef.current?.click();
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleUpload = async () => {
        if (!selectedFile || !user) return;

        setIsUploading(true);
        setError(null);
        setSuccess(null);

        try {
            // Check if the encrypted image storage service is ready
            if (!isServiceReady) {
                throw new Error('Image storage service is not ready yet. Please wait for initialization to complete.');
            }

            // Check if the TeamsPhotoService is ready
            if (!teamsPhotoService.isReady()) {
                throw new Error('Teams photo service is not ready yet. Please wait for authentication to complete.');
            }

            // Upload encrypted image to Azure Blob Storage via Firebase Functions
            const uploadResult = await encryptedImageStorageService.uploadEncryptedImage(
                selectedFile,
                challengeId,
                user.id,
                {
                    caption: caption.trim() || '',
                    originalFileName: selectedFile.name,
                    uploadTimestamp: new Date().toISOString()
                }
            );

            // Get the TeamsPhotoFirebaseService from the wrapper
            const teamsPhotoFirebaseService = teamsPhotoService.getTeamsPhotoFirebaseService();

            // Create photo post metadata for Firebase
            const photoPost = {
                challengeId,
                userId: user.id,
                userDisplayName: user.displayName || user.email || 'Unknown User',
                photoUrl: uploadResult.encryptedUrl,
                blobName: uploadResult.blobName, // Store blob name separately
                thumbnailUrl: uploadResult.thumbnailUrl, // Add thumbnail URL
                thumbnailBlobName: uploadResult.thumbnailBlobName, // Add thumbnail blob name
                teamsMessageId: `azure-${Date.now()}`, // Mock Teams message ID for Azure storage
                teamsChannelId: `challenge-${challengeId}`, // Mock Teams channel ID
                caption: caption.trim() || undefined,
                postedAt: new Date().toISOString(),
                likes: 0,
                comments: [],
                // Store encryption metadata for decryption
                encryptionKey: uploadResult.metadata.encryptionKey,
                encryptionAlgorithm: uploadResult.metadata.encryptionAlgorithm,
                ivLength: uploadResult.metadata.ivLength?.toString(),
                originalContentType: uploadResult.metadata.originalContentType,
                originalSize: uploadResult.metadata.originalSize?.toString(),
                optimizedSize: uploadResult.metadata.optimizedSize?.toString(),
                compressionRatio: uploadResult.metadata.compressionRatio
            };

            // Save photo metadata to Firebase
            const photoId = await teamsPhotoFirebaseService.savePhotoPost(photoPost);

            // Store optimization stats for display
            setOptimizationStats(uploadResult.optimizationStats);

            setSuccess('Photo uploaded and encrypted successfully!');
            setCaption('');
            setSelectedFile(null);
            setPreviewUrl(null);
            setShowUploadForm(false);

            // Notify parent component with the saved photo ID
            if (onPhotoUploaded) {
                onPhotoUploaded(photoId);
            }

            // Clean up preview URL
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }

            console.log('Encrypted image uploaded successfully:', {
                photoId,
                blobName: uploadResult.blobName,
                encryptedUrl: uploadResult.encryptedUrl,
                optimization: uploadResult.optimizationStats
            });

        } catch (error: any) {
            setError(error.message || 'Failed to upload encrypted image');
            console.error('Encrypted image upload failed:', error);
        } finally {
            setIsUploading(false);
        }
    };

    const handleCancel = () => {
        setSelectedFile(null);
        setCaption('');
        setShowUploadForm(false);
        setError(null);
        setSuccess(null);
        setOptimizationStats(null);

        // Clean up preview URL
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setCaption('');
        setShowUploadForm(false);
        setError(null);
        setSuccess(null);
        setOptimizationStats(null);

        // Clean up preview URL
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
    };

    return (
        <div className={`space-y-4 ${className}`}>
            {/* Secure Photo Storage Info */}
            {!showUploadForm && (
                <div className={`border rounded-lg p-3 ${isServiceReady
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                    }`}>
                    {isServiceReady ? (
                        <div className="flex items-center space-x-3 text-xs flex-wrap">
                            <div className="flex items-center space-x-1">
                                <Shield className="h-3 w-3 text-green-500 dark:text-green-400" />
                                <span className="text-gray-700 dark:text-gray-300">Encrypted</span>
                            </div>
                            <div className="flex items-center space-x-1">
                                <Zap className="h-3 w-3 text-blue-500 dark:text-blue-400" />
                                <span className="text-gray-700 dark:text-gray-300">Optimized</span>
                            </div>
                            <div className="flex items-center space-x-1">
                                <Info className="h-3 w-3 text-purple-500 dark:text-purple-400" />
                                <span className="text-gray-700 dark:text-gray-300">Private</span>
                            </div>
                            <div className="flex items-center space-x-1">
                                <Shield className="h-3 w-3 text-purple-500 dark:text-purple-400" />
                                <span className="text-gray-700 dark:text-gray-300">Secure</span>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center space-x-2 text-xs text-blue-700 dark:text-blue-300">
                            <svg className="h-3 w-3 animate-spin" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <span>Setting up secure storage...</span>
                        </div>
                    )}
                </div>
            )}

            {/* File Selection */}
            {!showUploadForm && (
                <div className="flex space-x-3">
                    <button
                        onClick={handleCameraClick}
                        style={{
                            border: 'none',
                            borderRadius: '999px',
                            padding: '8px 18px',
                            fontSize: '15px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: '#ffffff',
                            boxShadow: '0 8px 20px rgba(102, 126, 234, 0.35)',
                            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            overflow: 'hidden',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            flex: 1,
                            justifyContent: 'center',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 12px 28px rgba(102, 126, 234, 0.45)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.35)';
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: '-100%',
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
                                animation: 'shine 2s infinite',
                            }}
                        />
                        <Camera className="h-5 w-5" style={{ position: 'relative', zIndex: 1 }} />
                        <span style={{ position: 'relative', zIndex: 1 }}>Take Photo</span>
                    </button>
                    <button
                        onClick={handleUploadClick}
                        style={{
                            border: 'none',
                            borderRadius: '999px',
                            padding: '8px 18px',
                            fontSize: '15px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: '#ffffff',
                            boxShadow: '0 8px 20px rgba(102, 126, 234, 0.35)',
                            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            overflow: 'hidden',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            flex: 1,
                            justifyContent: 'center',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 12px 28px rgba(102, 126, 234, 0.45)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.35)';
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: '-100%',
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
                                animation: 'shine 2s infinite',
                            }}
                        />
                        <Upload className="h-5 w-5" style={{ position: 'relative', zIndex: 1 }} />
                        <span style={{ position: 'relative', zIndex: 1 }}>Upload Photo</span>
                    </button>
                </div>
            )}

            {/* Hidden File Input */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
            />

            {/* Upload Form */}
            {showUploadForm && selectedFile && (
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-800">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                            Upload Secure Photo
                        </h3>
                        <button
                            onClick={handleCancel}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* File Preview */}
                    <div className="mb-4">
                        <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                                <img
                                    src={previewUrl!}
                                    alt="Preview"
                                    className="h-16 w-16 object-cover rounded-lg"
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                    {selectedFile.name}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                                    <div>Size: {(selectedFile.size / 1024).toFixed(1)} KB</div>
                                    <div>Type: {selectedFile.type}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Caption Input */}
                    <div className="mb-4">
                        <label htmlFor="caption" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Caption (Optional)
                        </label>
                        <textarea
                            id="caption"
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            placeholder="Add a caption to your photo..."
                        />
                    </div>

                    {/* Upload Button */}
                    <div className="flex justify-end space-x-3">
                        <button
                            onClick={handleCancel}
                            style={{
                                border: '1px solid rgba(148, 163, 184, 0.5)',
                                borderRadius: '999px',
                                padding: '8px 18px',
                                fontSize: '15px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                backgroundColor: 'transparent',
                                color: 'inherit',
                                transition: 'all 0.25s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(148, 163, 184, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleUpload}
                            disabled={isUploading || !isServiceReady}
                            style={{
                                border: 'none',
                                borderRadius: '999px',
                                padding: '8px 18px',
                                fontSize: '15px',
                                fontWeight: '600',
                                cursor: isUploading || !isServiceReady ? 'not-allowed' : 'pointer',
                                backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: '#ffffff',
                                boxShadow: '0 8px 20px rgba(102, 126, 234, 0.35)',
                                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                position: 'relative',
                                overflow: 'hidden',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                opacity: isUploading || !isServiceReady ? 0.5 : 1,
                            }}
                            onMouseEnter={(e) => {
                                if (!isUploading && isServiceReady) {
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = '0 12px 28px rgba(102, 126, 234, 0.45)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.35)';
                            }}
                        >
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: '-100%',
                                    width: '100%',
                                    height: '100%',
                                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
                                    animation: 'shine 2s infinite',
                                }}
                            />
                            {isUploading ? (
                                <>
                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" style={{ position: 'relative', zIndex: 1 }}>
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span style={{ position: 'relative', zIndex: 1 }}>Securing & Uploading...</span>
                                </>
                            ) : (
                                <>
                                    <Send className="h-4 w-4" style={{ position: 'relative', zIndex: 1 }} />
                                    <span style={{ position: 'relative', zIndex: 1 }}>Upload Securely</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                                Upload Failed
                            </h3>
                            <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                                {error}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Message */}
            {success && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                                Upload Successful
                            </h3>
                            <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                                {success}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Optimization Stats */}
            {optimizationStats && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <Zap className="h-5 w-5 text-blue-400" />
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                                Photo Optimized
                            </h3>
                            <div className="mt-2 text-sm text-blue-700 dark:text-blue-300 space-y-1">
                                <div>Original: {(optimizationStats.originalSize / 1024).toFixed(1)} KB</div>
                                <div>Optimized: {(optimizationStats.optimizedSize / 1024).toFixed(1)} KB</div>
                                <div>Saved: {optimizationStats.compressionRatio.toFixed(1)}% space</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
